/**
 * Ceny energii i założenia net-billingu: JEDNO miejsce dla całego frontu.
 *
 * ═══ TO JEST PLIK DO RĘCZNEJ EDYCJI ═══
 * Te same liczby czytają kalkulator mocy (rachunek na kWh), opłacalność (zwrot), Projekt 3D,
 * oferta i karta klienta. Wcześniej cena 1,10 zł/kWh siedziała w kilku plikach i każda zmiana
 * groziła tym, że jeden ekran policzy zwrot po starej cenie. Backend ma tę samą cenę w stałej
 * `CENA_KWH` w `backend_api/dobor_magazynu.py` (przeliczenie rachunku w doborze magazynu);
 * zmieniając tutaj, zmień też tam.
 *
 * Ceny zakupu według taryfy (G11, G12, G12w, rozdział energii od dystrybucji) mieszkają w
 * `config/taryfy.js`. Tu zostają ceny wspólne: przeliczenie rachunku i wartość energii oddanej.
 *
 * Wszystko to są ZAŁOŻENIA, nie fakty: pokazujemy je użytkownikowi przy wyniku, a klient może
 * mieć własną cenę zakupu i sprzedaży w `dane.energia` (patrz `lib/klienci/energia.js`).
 */

import { KRAJ } from '../kraj'

/**
 * Cena 1 kWh do przeliczenia miesięcznego rachunku na zużycie (kalkulator mocy, karta klienta),
 * w walucie kraju wersji serwisu (nazwa została z czasów samej Polski). Oszczędność z prądu
 * zużytego na miejscu liczymy już ceną taryfy (`config/taryfy.js`), a nie tą liczbą.
 * Rumunia (2026-10-08): 1,30 lei, środek ofert rynkowych dla gospodarstw domowych z porównywarki
 * ANRE na 2026 rok (1,07–1,33 lei z dystrybucją i VAT); cena regulowana PPC to 1,50 lei. Poza
 * Polską ta liczba jest też ceną zakupu do rachunku (taryf innych krajów nie mamy).
 */
const CENY_ZAKUPU = { pl: 1.1, ro: 1.3 }
export const CENA_ZAKUPU_ZL_KWH = CENY_ZAKUPU[KRAJ.kod] ?? CENY_ZAKUPU.pl

/* ═══ Energia oddana do sieci (net-billing) ═════════════════════════════════════════════════
   Źródła i wyliczenie: `VV/dane_badawcze/taryfy_2026/net_billing_2026.md` (ustawa o OZE, Dz.U. 2026
   poz. 68; ceny PSE). Stan na 2026-10-23. */

/**
 * Wartość kWh oddanego do sieci przed współczynnikiem 1,23, w zł/kWh netto: rynkowa cena energii
 * ważona tym, kiedy dom oddaje nadwyżkę (głównie IV–IX, w południe). Dwa okresy z danych PSE
 * wyznaczają przedział: rok 2025 i ostatnie 12 miesięcy (X 2025 – IX 2026). Dla RCE (ceny
 * kwadransowe) i RCEm (miesięczne) wychodzi praktycznie to samo, więc jedna para liczb.
 */
export const RCE_WAZONA_EKSPORTEM_ZL_KWH = Object.freeze({ od: 0.245, do: 0.268 })

/** Art. 4c ust. 4 ustawy o OZE: wartość depozytu zwiększa się o 1,23 (od 1.02.2025). */
export const WSPOLCZYNNIK_DEPOZYTU = 1.23

/**
 * Wartość kWh oddanego w depozycie (z 1,23): środek przedziału, a przedział idzie do wyniku
 * jako zakres. Nazwa „cena sprzedaży” została z wcześniejszego modelu.
 */
export const CENA_SPRZEDAZY_OD_ZL_KWH = RCE_WAZONA_EKSPORTEM_ZL_KWH.od * WSPOLCZYNNIK_DEPOZYTU
export const CENA_SPRZEDAZY_DO_ZL_KWH = RCE_WAZONA_EKSPORTEM_ZL_KWH.do * WSPOLCZYNNIK_DEPOZYTU
export const CENA_SPRZEDAZY_ZL_KWH = Math.round(
  ((CENA_SPRZEDAZY_OD_ZL_KWH + CENA_SPRZEDAZY_DO_ZL_KWH) / 2) * 1000,
) / 1000

/**
 * Jak rozlicza się energia oddana. Kto pierwszy raz oddał energię po 30.06.2024, ma RCE (każdy nowy
 * klient); RCEm zostaje tylko starszym instalacjom (art. 4b). Depozyt idzie wyłącznie na energię
 * (nie na dystrybucję i opłaty), ważny 12 miesięcy; niewykorzystaną resztę sprzedawca zwraca, ale
 * najwyżej do 30% wartości energii oddanej przy RCE i 20% przy RCEm (art. 4 ust. 11 i 11a).
 * Reszta przepada. Czy limit liczy się od wartości z 1,23, ustawa nie mówi wprost: liczymy od niej.
 */
export const SYSTEMY_ROZLICZEN = Object.freeze([
  Object.freeze({ key: 'rce', label: 'RCE (instalacje od lipca 2024 r.)', procentZwrotu: 0.3 }),
  Object.freeze({ key: 'rcem', label: 'RCEm (starsze instalacje)', procentZwrotu: 0.2 }),
])
export const SYSTEM_ROZLICZEN_DOMYSLNY = 'rce'

/**
 * Roczny wzrost cen zakupu energii w scenariuszu domyślnym (obok zawsze liczymy scenariusz bez wzrostu).
 */
export const WZROST_CEN_DOMYSLNY = 0.03

/**
 * Cena rynkowa (sprzedaży) też rośnie, ale wolniej niż detaliczna: w cenie u odbiorcy siedzą
 * opłaty i dystrybucja, których hurtowa nie zawiera. Przyjmujemy taki ułamek tempa wzrostu zakupu.
 */
export const UDZIAL_WZROSTU_CENY_SPRZEDAZY = 0.5

/**
 * O ile produkcja w roku słabszym i lepszym różni się od średniej: odchylenie standardowe rocznej
 * produkcji z PVGIS (SARAH3, lata 2005–2023) dla środków 16 województw, moduły na południe pod 35°,
 * wynosi 4,5–5,6%, średnio 5,1% (API PVGIS 5.3, 2026-10-23, `net_billing_2026.md`).
 */
export const ZMIENNOSC_PRODUKCJI = 0.05
